// Moteur leger pour les calculs courants. Le moteur mathjs complet est charge
// a la demande lorsqu'une fonction scientifique ou une structure avancee est
// detectee.

const NATURAL_REPLACEMENTS = [
  [/\bde\b/gi, '*'],
  [/\bpour\s*cent\b/gi, '%'],
  [/\bfois\b/gi, '*'],
  [/\bplus\b/gi, '+'],
  [/\bmoins\b/gi, '-'],
  [/\bdivise\s*par\b/gi, '/'],
  [/\bsur\b/gi, '/'],
  [/\bracine\s*(carree)?\s*(de)?\b/gi, 'sqrt'],
  [/\s*\ben\s*/gi, ' to '],
  [/Ã—/g, '*'],
  [/Ã·/g, '/'],
  [/Ï€/g, 'pi'],
  [/âˆš/g, 'sqrt'],
];

const UNIT_ALIASES = [
  [/\bheures?\b/gi, 'hours'],
  [/\bminutes?\b/gi, 'minutes'],
  [/\bsecondes?\b/gi, 'seconds'],
  [/\bjours?\b/gi, 'days'],
  [/\bmetres?\b/gi, 'meters'],
  [/\bkilometres?\b/gi, 'km'],
  [/\bcentimetres?\b/gi, 'cm'],
  [/\bgrammes?\b/gi, 'grams'],
  [/\bkilogrammes?\b/gi, 'kg'],
];

const SIMPLE_UNIT_FACTORS = {
  seconds: 1,
  minutes: 60,
  hours: 3600,
  days: 86400,
  grams: 1,
  kg: 1000,
  meters: 1,
  cm: 0.01,
  km: 1000,
};

const BASIC_NAMES = new Set(['pi', 'e', 'ans']);

export function normalizeLite(input) {
  let expr = String(input);
  for (const [pattern, replacement] of UNIT_ALIASES) {
    expr = expr.replace(pattern, replacement);
  }
  for (const [pattern, replacement] of NATURAL_REPLACEMENTS) {
    expr = expr.replace(pattern, replacement);
  }
  expr = expr.replace(/(\d|\))\s*(?=pi\b)/gi, '$1 * ');
  expr = expr.replace(/(\d|\))\s*(?=e\b)/gi, '$1 * ');
  expr = expr.replace(/(\d|\))\s*(?=ans\b)/gi, '$1 * ');
  expr = expr.replace(/(\d|\))\s*(?=\()/g, '$1 * ');
  return expr.trim();
}

function convertSimpleUnit(expression) {
  const match = String(expression).match(
    /^(-?(?:\d+\.?\d*|\.\d+))\s+(seconds|minutes|hours|days|grams|kg|meters|cm|km)\s+to\s+(seconds|minutes|hours|days|grams|kg|meters|cm|km)$/i
  );
  if (!match) return null;
  const value = Number(match[1]);
  const from = match[2].toLowerCase();
  const to = match[3].toLowerCase();
  const timeUnits = ['seconds', 'minutes', 'hours', 'days'];
  const massUnits = ['grams', 'kg'];
  const family = (unit) =>
    timeUnits.includes(unit)
      ? 'time'
      : massUnits.includes(unit)
        ? 'mass'
        : 'length';
  if (family(from) !== family(to)) return null;
  return (value * SIMPLE_UNIT_FACTORS[from]) / SIMPLE_UNIT_FACTORS[to];
}

function tokenize(expression) {
  const tokens = [];
  const pattern =
    /\s*(?:(\d+(?:\.\d*)?(?:e[+-]?\d+)?|\.\d+(?:e[+-]?\d+)?)|([A-Za-z_]\w*)|([()+\-*/^%]))/iy;
  let index = 0;
  while (index < expression.length) {
    pattern.lastIndex = index;
    const match = pattern.exec(expression);
    if (!match || match.index !== index) {
      throw new Error('Expression invalide');
    }
    index = pattern.lastIndex;
    tokens.push(
      match[1]
        ? { type: 'number', value: Number(match[1]) }
        : match[2]
          ? { type: 'name', value: match[2] }
          : { type: 'operator', value: match[3] }
    );
  }
  return tokens;
}

function parseBasic(expression, scope) {
  const tokens = tokenize(expression);
  let cursor = 0;

  const peek = () => tokens[cursor];
  const take = () => tokens[cursor++];
  const match = (value) => peek()?.value === value && Boolean(take());

  function primary() {
    const token = take();
    if (!token) throw new Error('Expression invalide');
    if (token.type === 'number') return token.value;
    if (token.type === 'name') {
      if (BASIC_NAMES.has(token.value)) {
        return token.value === 'pi'
          ? Math.PI
          : token.value === 'e'
            ? Math.E
            : (scope.ans ?? 0);
      }
      if (Object.prototype.hasOwnProperty.call(scope, token.value)) {
        return scope[token.value];
      }
      throw Object.assign(new Error('Moteur scientifique requis'), {
        needsFull: true,
      });
    }
    if (token.value === '(') {
      let value = addition();
      if (!match(')')) throw new Error('Parenthese manquante');
      return value;
    }
    throw new Error('Expression invalide');
  }

  function unary() {
    if (match('+')) return unary();
    if (match('-')) return -unary();
    let value = primary();
    while (match('%')) value /= 100;
    return value;
  }

  function power() {
    const left = unary();
    if (match('^')) return left ** power();
    return left;
  }

  function product() {
    let value = power();
    while (peek()?.value === '*' || peek()?.value === '/') {
      const operator = take().value;
      const right = power();
      value = operator === '*' ? value * right : value / right;
    }
    return value;
  }

  function addition() {
    let value = product();
    while (peek()?.value === '+' || peek()?.value === '-') {
      const operator = take().value;
      const right = product();
      value = operator === '+' ? value + right : value - right;
    }
    return value;
  }

  let value = addition();
  while (match('%')) value /= 100;
  if (cursor !== tokens.length) {
    const token = peek();
    if (token?.type === 'name') {
      throw Object.assign(new Error('Moteur scientifique requis'), {
        needsFull: true,
      });
    }
    throw new Error('Expression invalide');
  }
  return value;
}

function hasUnsupportedSyntax(expression, scope) {
  const withoutNumbers = expression.replace(
    /\d+(?:\.\d*)?(?:e[+-]?\d+)?|\.\d+(?:e[+-]?\d+)?/gi,
    ''
  );
  const names = withoutNumbers.match(/[A-Za-z_]\w*/g) || [];
  return names.some(
    (name) =>
      !BASIC_NAMES.has(name) &&
      !Object.prototype.hasOwnProperty.call(scope, name)
  );
}

export function evaluateLite(input, scope = {}, options = {}) {
  const expr = normalizeLite(input);
  if (!expr) return { ok: false, error: 'Expression vide', scope };

  const workingScope = {
    ...scope,
    ...(options.ans !== undefined ? { ans: options.ans } : {}),
  };
  const assignment = expr.match(/^([A-Za-z_]\w*)\s*=\s*(.+)$/);
  const source = assignment ? assignment[2] : expr;

  try {
    const converted = convertSimpleUnit(expr);
    if (converted !== null) {
      return {
        ok: true,
        value: converted,
        text: formatLite(converted, options.precision),
        scope: workingScope,
      };
    }
    if (hasUnsupportedSyntax(source, workingScope)) return { needsFull: true };
    const value = parseBasic(source, workingScope);
    if (!Number.isFinite(value))
      return {
        ok: true,
        value,
        text: formatLite(value, options.precision),
        scope: workingScope,
      };
    if (assignment) workingScope[assignment[1]] = value;
    return {
      ok: true,
      value,
      text: formatLite(value, options.precision),
      scope: workingScope,
    };
  } catch (error) {
    if (error.needsFull) return { needsFull: true };
    return { ok: false, error: error.message || 'Erreur', scope };
  }
}

export function formatLite(value, precision = 12) {
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean') return value ? 'vrai' : 'faux';
  if (typeof value === 'number') {
    if (Number.isNaN(value)) return 'NaN';
    if (!Number.isFinite(value)) return value > 0 ? 'âˆž' : '-âˆž';
    return String(Number(value.toPrecision(precision)));
  }
  return String(value);
}

export function isFullEngineRequired(result) {
  return Boolean(result && result.needsFull);
}
